import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Leaf,
  Droplets,
  Settings,
  BarChart3,
  Sprout,
  Globe2,
  Thermometer,
  FlaskConical,
  Gauge,
  Wifi,
  CheckCircle2,
  Smartphone,
  Sun,
  Wind,
  ShieldCheck,
  CalendarDays,
  Coins,
  Play,
} from "lucide-react";

// =====================================================
// IMAGES
// Put these images inside: src/image/
// =====================================================

import TechnologyHero from "../image/technology-hero.png";
import IoTSensor from "../image/iot-sensors.png";
import PHMonitoring from "../image/ph-monitoring.png";
import ECMonitoring from "../image/ec-monitoring.png";
import Automation from "../image/automation.png";
import EnvironmentalControl from "../image/environmental-control.png";
import DataAnalytics from "../image/data-analytics.png";
import DashboardImage from "../image/farm-dashboard.png";
import GrowingImage from "../image/optimal-growing.png";


// =====================================================
// TECHNOLOGY CARDS
// =====================================================

const technologyCards = [
  {
    title: "IoT Sensors",
    image: IoTSensor,
    icon: Wifi,
    description:
      "Continuous monitoring of pH, EC, temperature, humidity and more.",
  },
  {
    title: "pH Monitoring",
    image: PHMonitoring,
    icon: FlaskConical,
    description:
      "Maintain the perfect nutrient balance for healthy plant growth.",
  },
  {
    title: "EC Monitoring",
    image: ECMonitoring,
    icon: Gauge,
    description:
      "Track nutrient concentration in real time for optimal growth.",
  },
  {
    title: "Automation",
    image: Automation,
    icon: Settings,
    description:
      "Automated control of pumps, lights, climate and nutrient dosing.",
  },
  {
    title: "Environmental Control",
    image: EnvironmentalControl,
    icon: Thermometer,
    description:
      "Temperature, humidity, lighting, CO₂ and ventilation management.",
  },
  {
    title: "Data Analytics",
    image: DataAnalytics,
    icon: BarChart3,
    description:
      "Turn farm data into insights for better decisions and higher yields.",
  },
];


// =====================================================
// KEY TECHNOLOGIES
// =====================================================

const keyTechnologies = [
  {
    icon: Settings,
    title: "Smart Sensors",
    description:
      "Measure and monitor essential parameters",
  },
  {
    icon: Droplets,
    title: "Nutrient Dosing",
    description:
      "Automated nutrient mixing and delivery",
  },
  {
    icon: Wind,
    title: "Climate Control",
    description:
      "Temperature, humidity, ventilation and CO₂",
  },
  {
    icon: Sun,
    title: "LED Lighting",
    description:
      "Energy-efficient grow lights",
  },
  {
    icon: BarChart3,
    title: "Data Analytics",
    description:
      "Insights and recommendations",
  },
  {
    icon: Smartphone,
    title: "Remote Access",
    description:
      "Monitor and control from anywhere",
  },
];


// =====================================================
// BENEFITS
// =====================================================

const benefits = [
  {
    icon: BarChart3,
    number: "30–50%",
    title: "Higher Yield",
  },
  {
    icon: Droplets,
    number: "90%",
    title: "Less Water",
  },
  {
    icon: Leaf,
    number: "0%",
    title: "Soil & Pesticides",
  },
  {
    icon: Coins,
    number: "Lower",
    title: "Operational Cost",
  },
  {
    icon: ShieldCheck,
    number: "Better",
    title: "Crop Quality",
  },
  {
    icon: CalendarDays,
    number: "Year-Round",
    title: "Production",
  },
];


// =====================================================
// COMPONENT
// =====================================================

export default function Technology() {
  return (
    <div className="min-h-screen bg-white text-[#10231c]">

      {/* =================================================
          HERO
      ================================================= */}
      <section className="relative overflow-hidden">

        <div className="relative min-h-[500px] lg:min-h-[535px]">

          {/* Background Image */}
          <img
            src={TechnologyHero}
            alt="Smart hydroponic farming technology"
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* Left White Gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/10" />

          {/* Soft Green */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#f5fbf6]/70 via-transparent to-transparent" />

          {/* Hero Content */}
          <div className="relative z-10 mx-auto flex min-h-[500px] max-w-[1400px] items-center px-5 py-16 lg:min-h-[535px] lg:px-10">

            <div className="max-w-[550px]">

              {/* Small Heading */}
              <p className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                Smart Farming Technology
              </p>

              {/* Main Heading */}
              <h1 className="text-4xl font-extrabold leading-[1.04] tracking-tight sm:text-5xl lg:text-[52px]">

                Technology
                <br />

                for{" "}
                <span className="text-[#087f4f]">
                  Smarter,
                </span>
                <br />

                <span className="text-[#087f4f]">
                  Healthier Farming
                </span>

              </h1>

              {/* Description */}
              <p className="mt-5 max-w-[500px] text-base leading-6 text-gray-600">
                We use advanced IoT sensors, automation and data
                analytics to create the perfect growing environment
                for high-quality hydroponic crops with less water,
                less effort and higher yields.
              </p>


              {/* Buttons */}
              <div className="mt-7 flex flex-wrap gap-3">

                <a
                  href="#technology"
                  className="inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#066b42]"
                >
                  Explore Our Technology
                  <ArrowRight size={16} />
                </a>

                <button
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-bold text-gray-700 shadow-sm"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#e9f7ef] text-[#087f4f]">
                    <Play size={11} fill="currentColor" />
                  </span>

                  Watch Video
                </button>

              </div>

            </div>


            {/* =================================================
                SENSOR FLOATING BOXES
            ================================================= */}

            <div className="absolute right-[38%] top-8 hidden rounded-xl bg-white/95 px-5 py-3 shadow-lg lg:block">
              <div className="flex items-center gap-3">
                <Thermometer className="text-[#087f4f]" size={26} />

                <div>
                  <p className="text-[10px] text-gray-500">
                    Temperature
                  </p>

                  <p className="text-sm font-bold">
                    24.5°C
                  </p>
                </div>
              </div>
            </div>


            <div className="absolute right-[7%] top-8 hidden rounded-xl bg-white/95 px-5 py-3 shadow-lg lg:block">
              <div className="flex items-center gap-3">
                <Droplets className="text-blue-500" size={26} />

                <div>
                  <p className="text-[10px] text-gray-500">
                    Humidity
                  </p>

                  <p className="text-sm font-bold">
                    68%
                  </p>
                </div>
              </div>
            </div>


            <div className="absolute right-[38%] bottom-28 hidden rounded-xl bg-white/95 px-5 py-3 shadow-lg lg:block">
              <div className="flex items-center gap-3">
                <Leaf className="text-[#087f4f]" size={27} />

                <div>
                  <p className="text-[10px] text-gray-500">
                    pH
                  </p>

                  <p className="text-sm font-bold">
                    5.8
                  </p>
                </div>
              </div>
            </div>


            <div className="absolute right-[20%] bottom-8 hidden rounded-xl bg-white/95 px-5 py-3 shadow-lg lg:block">
              <div className="flex items-center gap-3">
                <FlaskConical
                  className="text-[#087f4f]"
                  size={26}
                />

                <div>
                  <p className="text-[10px] text-gray-500">
                    EC
                  </p>

                  <p className="text-sm font-bold">
                    1.4 mS/cm
                  </p>
                </div>
              </div>
            </div>


            {/* Right Green Panel */}
            <div className="absolute bottom-0 right-0 hidden h-[240px] w-[280px] overflow-hidden rounded-tl-[130px] bg-[#005536] lg:block">

              <div className="flex h-full flex-col justify-center px-10">

                <p
                  className="text-center text-3xl font-semibold italic leading-tight text-white"
                  style={{
                    fontFamily: "Georgia, serif",
                  }}
                >
                  Smart
                  <br />
                  Farming
                  <br />
                  <span className="text-2xl">
                    Real Results
                  </span>
                </p>

                <div className="mx-auto mt-3 h-[3px] w-36 rotate-[-5deg] rounded-full bg-[#76d23d]" />

              </div>

            </div>

          </div>

        </div>
      </section>


      {/* =================================================
          BENEFIT STRIP
      ================================================= */}
      <section className="border-y border-green-100 bg-[#f4faf6]">

        <div className="mx-auto grid max-w-[1400px] grid-cols-2 px-5 lg:grid-cols-6 lg:px-10">

          <BenefitStrip
            icon={<Leaf size={27} />}
            text="Real-Time Monitoring"
          />

          <BenefitStrip
            icon={<Settings size={27} />}
            text="Automated Control"
          />

          <BenefitStrip
            icon={<BarChart3 size={27} />}
            text="Data-Driven Insights"
          />

          <BenefitStrip
            icon={<Droplets size={27} />}
            text="Resource Efficient"
          />

          <BenefitStrip
            icon={<Sprout size={27} />}
            text="Higher Productivity"
          />

          <BenefitStrip
            icon={<Globe2 size={27} />}
            text="Sustainable Farming"
          />

        </div>

      </section>


      {/* =================================================
          COMPLETE TECHNOLOGY
      ================================================= */}
      <section
        id="technology"
        className="bg-white py-12 lg:py-14"
      >

        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

          {/* Heading */}
          <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">

            <div>

              <p className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                Our Technology
              </p>

              <h2 className="text-3xl font-extrabold sm:text-4xl">
                Complete Smart Farming Solutions
              </h2>

              <p className="mt-2 max-w-[680px] text-sm leading-6 text-gray-600">
                From real-time monitoring to automated control,
                our technology helps you grow healthier plants with
                higher yields using intelligent and sustainable farming methods.
              </p>

            </div>


            <Link
              to="/contact"
              className="inline-flex w-fit items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white"
            >
              Talk to Our Experts
              <ArrowRight size={16} />
            </Link>

          </div>


          {/* Cards */}
          <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">

            {technologyCards.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="group overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                >

                  {/* Image */}
                  <div className="h-[125px] overflow-hidden">

                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                  </div>


                  {/* Content */}
                  <div className="p-3">

                    <div className="flex items-center gap-2">

                      <Icon
                        size={18}
                        className="text-[#087f4f]"
                      />

                      <h3 className="text-sm font-bold">
                        {item.title}
                      </h3>

                    </div>

                    <p className="mt-2 text-xs leading-5 text-gray-500">
                      {item.description}
                    </p>

                    <Link
                      to="/contact"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#087f4f]"
                    >
                      Learn More
                      <ArrowRight size={13} />
                    </Link>

                  </div>

                </div>
              );
            })}

          </div>

        </div>

      </section>


      {/* =================================================
          LIVE MONITORING DASHBOARD
      ================================================= */}
      <section className="relative overflow-hidden bg-[#004f35] py-10 lg:py-12">

        {/* Background glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#004b31] via-[#00633f] to-[#004a31]" />

        <div className="relative mx-auto grid max-w-[1400px] items-center gap-8 px-5 lg:grid-cols-[0.8fr_1.2fr] lg:px-10">

          {/* Content */}
          <div>

            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#77d45b]">
              Live Monitoring Dashboard
            </p>

            <h2 className="mt-1 text-3xl font-extrabold text-white">
              Real-Time Farm Insights
            </h2>

            <p className="mt-3 max-w-[480px] text-sm leading-6 text-white/80">
              Monitor and control your farm anytime, anywhere.
              Our IoT-enabled system collects real-time data and
              provides actionable insights through a simple and
              easy-to-use dashboard.
            </p>


            {/* Checklist */}
            <div className="mt-5 space-y-2">

              {[
                "Live sensor data (pH, EC, Temp, Humidity, CO₂)",
                "Device control (pumps, lights, fans, dosing)",
                "Historical data & trend analysis",
                "Alerts & notifications",
                "Multi-location farm management",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2 text-sm text-white/90"
                >
                  <CheckCircle2
                    size={17}
                    className="text-white"
                  />

                  {item}
                </div>
              ))}

            </div>


            {/* Buttons */}
            <div className="mt-6 flex flex-wrap gap-3">

              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white shadow-lg"
              >
                View Live Dashboard
                <ArrowRight size={16} />
              </Link>

              <button
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#087f4f]"
              >
                <Play size={14} fill="currentColor" />
                Request a Demo
              </button>

            </div>

          </div>


          {/* Dashboard Image */}
          <div className="overflow-hidden rounded-2xl border-2 border-white/30 bg-black/20 shadow-2xl">

            <img
              src={DashboardImage}
              alt="Smart farm monitoring dashboard"
              className="w-full object-cover"
            />

          </div>

        </div>

      </section>


      {/* =================================================
          KEY TECHNOLOGIES
      ================================================= */}
      <section className="bg-white py-10 lg:py-12">

        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

          <div className="grid gap-8 lg:grid-cols-[1fr_300px]">

            {/* Left */}
            <div>

              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#16854f]">
                Key Technologies
              </p>

              <h2 className="mt-1 text-2xl font-extrabold sm:text-3xl">
                Building Blocks of Smarter Farming
              </h2>

              <p className="mt-2 text-sm text-gray-600">
                Our integrated technologies work together to create
                the ideal environment for plant growth.
              </p>


              {/* Technology Cards */}
              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">

                {keyTechnologies.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="rounded-xl bg-[#f7faf8] p-4 text-center"
                    >

                      <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
                        <Icon size={22} />
                      </div>

                      <h3 className="mt-3 text-xs font-bold">
                        {item.title}
                      </h3>

                      <p className="mt-1 text-[10px] leading-4 text-gray-500">
                        {item.description}
                      </p>

                    </div>
                  );
                })}

              </div>

            </div>


            {/* Optimal Environment */}
            <div className="flex m-auto  overflow-hidden rounded-2xl bg-[#f0faf2] ">

             


              <img
                src={GrowingImage}
                alt="Optimal growing environment"
                className=" object-cover opacity-90"
              />

            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          BENEFITS
      ================================================= */}
      <section className="bg-white pb-10">

        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#16854f]">
            Why Smart Farming?
          </p>

          <h2 className="mt-1 text-2xl font-extrabold sm:text-3xl">
            Real Benefits for Farmers and Businesses
          </h2>


          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">

            {benefits.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="flex items-center gap-3 border-r border-gray-100 last:border-0"
                >

                  <Icon
                    size={31}
                    className="flex-shrink-0 text-[#087f4f]"
                  />

                  <div>

                    <p className="text-lg font-extrabold">
                      {item.number}
                    </p>

                    <p className="text-xs text-gray-500">
                      {item.title}
                    </p>

                  </div>

                </div>
              );
            })}

          </div>

        </div>

      </section>


      {/* =================================================
          CTA
      ================================================= */}
      <section className="relative overflow-hidden bg-[#005536]">

        <div className="absolute inset-0 bg-gradient-to-r from-[#00482f] to-[#087f4f]" />

        <div className="relative mx-auto flex max-w-[1400px] flex-col justify-between gap-5 px-5 py-7 sm:flex-row sm:items-center lg:px-10">

          <div>

            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#77d45b]">
              Ready to Upgrade Your Farm?
            </p>

            <h2 className="mt-1 text-2xl font-extrabold text-white sm:text-3xl">
              Let's Build a Smarter, Greener Future Together
            </h2>

            <p className="mt-1 text-sm text-white/80">
              Get expert guidance on the right technology for your farming needs.
            </p>

          </div>


          <Link
            to="/contact"
            className="inline-flex w-fit flex-shrink-0 items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#087f4f]"
          >
            Get a Custom Solution
            <ArrowRight size={16} />
          </Link>

        </div>

      </section>

    </div>
  );
}


// =====================================================
// BENEFIT STRIP
// =====================================================

function BenefitStrip({ icon, text }) {
  return (
    <div className="flex items-center gap-3 border-b border-green-100 px-3 py-4 lg:border-b-0 lg:border-r">

      <span className="text-[#087f4f]">
        {icon}
      </span>

      <span className="text-xs font-bold text-gray-700">
        {text}
      </span>

    </div>
  );
}


